/**
 * MixedActionTable.utils — derived-data hook for MixedActionTable.
 *
 * Encapsulates the visible-actions/association-count computation, the
 * homogeneous-vs-mixed mode detection, and the association-type /
 * property-schema fetches those modes depend on — kept out of the main
 * component so its render logic reads top-to-bottom.
 */

import { useMemo } from 'react';

import { useAssociationTypes } from '@/lib/crm/hooks/useAssociationTypes';
import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';

import type {
  ProposalDetail,
  ProposedAction,
} from '../../lib/proposals/core/types';
import { isTimelineEventPayload } from '../../lib/proposals/core/types';
import { STAGE_ASSOCIATION_TYPES_SET } from '../../lib/proposals/stageAssociationTypes';
import { mergeActions } from '../../lib/proposals/utils/actionMerging';
import { isWideMixedGroup } from './shared/utils';

export function useMixedActionTableData(
  actions: ProposedAction[],
  proposal: ProposalDetail
) {
  // Filter out association actions that are absorbed into a merged create card
  // so they don't also appear as separate rows in the table.
  // Also precompute association counts per create action for "+N links" badges.
  const { visibleActions, associationCountMap } = useMemo(() => {
    const merged = mergeActions(actions);
    const absorbedIds = new Set<number>();
    const countMap = new Map<number, number>();
    for (const item of merged) {
      if (item.type === 'merged') {
        const assocCount = item.merged.mergedAssociations.length;
        if (assocCount > 0) {
          countMap.set(item.merged.primaryAction.id, assocCount);
        }
        for (const assoc of item.merged.mergedAssociations) {
          absorbedIds.add(assoc.action.id);
        }
      }
    }
    const visible =
      absorbedIds.size > 0
        ? actions.filter(a => !absorbedIds.has(a.id))
        : actions;
    return { visibleActions: visible, associationCountMap: countMap };
  }, [actions]);

  // `isWideMixedGroup` is the single source of truth for this homogeneity
  // check — shared with `ProposalContent`/`MultiActionTimeline`'s decision on
  // whether the whole proposal should render full-bleed. See its doc comment
  // for why: only this "wide" shape (Name/Property/Current/Proposed columns)
  // benefits from the extra width; the default single-Action-column shape
  // does not, and stretching it (plus everything above it) just leaves dead
  // space.
  const { isHomogeneous, entityTypeName } = useMemo(() => {
    const homogeneous = isWideMixedGroup(visibleActions);
    if (!homogeneous) {
      return { isHomogeneous: false, entityTypeName: null };
    }
    // Re-derive the entity type for the label — `isWideMixedGroup` already
    // established every action shares one, so the first is representative.
    const firstPayload = visibleActions[0].payload;
    const firstEntityType = isTimelineEventPayload(firstPayload)
      ? firstPayload.entity_type_name
      : null;
    return { isHomogeneous: true, entityTypeName: firstEntityType };
  }, [visibleActions]);

  const firstVisiblePayload = visibleActions[0]?.payload;
  // Fetch association types when the table contains associations in
  // homogeneous mode OR when any action targets a hidden entity (mixed or
  // homogeneous). Hidden-entity stage-association editability depends on
  // dynamic stage types, so we need them even in mixed tables.
  const hasHiddenEntityActions = useMemo(
    () => visibleActions.some(a => a.enriched_data?.is_hidden),
    [visibleActions]
  );
  const needsAssociationTypes =
    hasHiddenEntityActions ||
    (isHomogeneous &&
      isTimelineEventPayload(firstVisiblePayload) &&
      (firstVisiblePayload.event_type_name === 'ahoy_association_added' ||
        firstVisiblePayload.event_type_name === 'ahoy_association_removed'));
  const { associationTypes, stageAssociationTypes } = useAssociationTypes(
    needsAssociationTypes ? proposal.org : undefined
  );
  const stableAssociationTypes = useMemo(
    () => associationTypes ?? [],
    [associationTypes]
  );
  // Merge dynamic stage types with hardcoded fallback so built-in stage
  // associations (contact lifecycle, deal pipeline) are always detected,
  // even if the API response lacks target_entity_kind_name metadata.
  const stableStageAssociationTypes = useMemo(
    () =>
      new Set([
        ...STAGE_ASSOCIATION_TYPES_SET,
        ...(stageAssociationTypes ?? []),
      ]),
    [stageAssociationTypes]
  );

  // Fetch property schema for type-aware value rendering in homogeneous mode
  const { schema: propertySchemaMap } = usePropertySchema(
    isHomogeneous ? proposal.org : 0,
    entityTypeName ?? ''
  );

  return {
    visibleActions,
    associationCountMap,
    isHomogeneous,
    entityTypeName,
    stableAssociationTypes,
    stableStageAssociationTypes,
    propertySchemaMap,
  };
}
